import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APPLICATION_STATUS_LABEL, ROLES, type ApplicationStatusCode } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtDate, fmtTime, titleCase } from '../lib/format';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented, Select, Textarea } from '../components/ui';
import { PolicyText } from './Apply';

/** Admin: enrolment queue and decisions (INT-ENR-01/02), invites, suspend/reactivate (INT-ENR-04), floor documents (INT-ADM-03). */

interface AppRow {
  id: string;
  name: string;
  email: string;
  country: string | null;
  status: ApplicationStatusCode;
  invited: boolean;
  invitedBy: string | null;
  marketsOfInterest: string[];
  experienceYears: number | null;
  createdAt: string;
  updatedAt: string;
  activated: boolean | null;
  queuePosition: number | null;
}
interface AppDetail {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  country: string | null;
  timezone: string;
  experienceYears: number | null;
  marketsOfInterest: string[];
  preferredSymbols: string[];
  brokers: string | null;
  statement: string | null;
  referral: string | null;
  status: ApplicationStatusCode;
  decisionReason: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
  invite: { invitedBy: string; email: string | null; createdAt: string } | null;
  user: { id: string; status: string; lastLoginAt: string | null } | null;
  activationExpiresAt: string | null;
  acceptances: { kind: string; label: string; version: number; acceptedAt: string }[];
  suggestedInstrumentIds: string[];
  history: { action: string; at: string; actor: string | null; detail: Record<string, unknown> | null }[];
}
interface AccountRow {
  id: string;
  code: string;
  name: string;
  currency: string;
  status: string;
  market: { id: string; code: string; name: string };
  traders: { id: string; name: string }[];
}
interface MarketRow {
  id: string;
  code: string;
  name: string;
  currency: string;
}
interface InstrumentRow {
  id: string;
  symbol: string;
  displayName: string;
  marketId: string;
}
interface InviteRow {
  id: string;
  email: string | null;
  name: string | null;
  marketsOfInterest: string[];
  note: string | null;
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
  state: 'OPEN' | 'USED' | 'EXPIRED';
  application: { id: string; name: string; status: string } | null;
}
interface MemberRow {
  id: string;
  name: string;
  email: string;
  status: 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'DEACTIVATED';
  roles: string[];
  lastLoginAt: string | null;
  totpEnabled: boolean;
  openTrades: number;
}
interface PolicyOverview {
  kind: string;
  label: string;
  current: { id: string; version: number; title: string; body: string; publishedAt: string } | null;
  acceptedMembers: number;
  activeMembers: number;
  versions: { id: string; version: number; title: string; publishedAt: string; isCurrent: boolean; acceptances: number }[];
}

const TABS = [
  { key: 'applications', label: 'Applications' },
  { key: 'invites', label: 'Invites' },
  { key: 'members', label: 'Members' },
  { key: 'documents', label: 'Floor documents' },
] as const;
type Tab = (typeof TABS)[number]['key'];

const statusTone = (s: string) => (s === 'APPROVED' || s === 'ACTIVE' ? 'up' : s === 'REJECTED' || s === 'SUSPENDED' ? 'down' : s === 'MORE_INFO' || s === 'INVITED' ? 'warn' : 'neutral') as 'up' | 'down' | 'warn' | 'neutral';
const roleLabel = (r: string) => titleCase(r);

function Ok({ text }: { text: string | null }) {
  return text ? <p className="m-0 text-sm text-up">{text}</p> : null;
}

export function Enrolment() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key ?? 'applications') as Tab;
  const counts = useQuery({ queryKey: ['applications', 'OPEN', ''], queryFn: () => api<{ counts: Record<string, number>; items: AppRow[] }>('/applications') });
  const waiting = counts.data?.items.length ?? 0;
  const setTab = (t: Tab) => setParams((p) => {
    const n = new URLSearchParams(p);
    n.set('tab', t);
    if (t !== 'applications') n.delete('application');
    return n;
  });

  return (
    <>
      <PageHeader title="Enrolment" sub="Applications, invites, members and the floor documents everyone accepts" />
      <div role="tablist" aria-label="Enrolment sections" className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === tab}
            onClick={() => setTab(t.key)}
            className={cx('cursor-pointer rounded-lg border px-3.5 py-2 text-sm', t.key === tab ? 'border-accent bg-accent-bg font-semibold text-ink' : 'border-line2 bg-panel text-sub')}
          >
            {t.label}
            {t.key === 'applications' && waiting > 0 && <span className="ml-1.5 rounded-full bg-warn px-1.5 text-xs font-bold text-bg">{waiting}</span>}
          </button>
        ))}
      </div>
      {tab === 'applications' && <Applications />}
      {tab === 'invites' && <Invites />}
      {tab === 'members' && <Members />}
      {tab === 'documents' && <Documents />}
    </>
  );
}

// ───────────────────────── Applications ─────────────────────────

function Applications() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<'OPEN' | 'APPROVED' | 'REJECTED' | 'ALL'>('OPEN');
  const [q, setQ] = useState('');
  const list = useQuery({ queryKey: ['applications', filter, q], queryFn: () => api<{ counts: Record<string, number>; items: AppRow[] }>(`/applications?status=${filter}${q ? `&q=${encodeURIComponent(q)}` : ''}`) });
  const selected = params.get('application');
  const select = (id: string) => setParams((p) => {
    const n = new URLSearchParams(p);
    n.set('application', id);
    return n;
  });
  const c = list.data?.counts ?? {};
  const open = (c.SUBMITTED ?? 0) + (c.UNDER_REVIEW ?? 0) + (c.MORE_INFO ?? 0);

  return (
    <div className="grid items-start gap-3.5 xl:grid-cols-[minmax(320px,2fr)_3fr]">
      <Card className="flex flex-col">
        <div className="flex flex-col gap-2.5 border-b border-line p-3">
          <Segmented
            label="Show"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'OPEN', label: `Waiting · ${open}` },
              { value: 'APPROVED', label: `Approved · ${c.APPROVED ?? 0}` },
              { value: 'REJECTED', label: `Not approved · ${c.REJECTED ?? 0}` },
              { value: 'ALL', label: 'All' },
            ]}
          />
          <Input aria-label="Search by name or email" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <ErrorText error={list.error ? errorText(list.error) : null} />
        {list.data && !list.data.items.length && <Empty>{filter === 'OPEN' ? 'No applications waiting.' : 'Nothing here.'}</Empty>}
        <ul aria-label="Applications" className="m-0 flex max-h-[70vh] list-none flex-col overflow-y-auto p-0">
          {list.data?.items.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => select(a.id)}
                aria-current={a.id === selected ? 'true' : undefined}
                className={cx('flex w-full cursor-pointer flex-col gap-1 border-t border-[#1a1f27] px-4 py-3 text-left first:border-t-0', a.id === selected ? 'bg-accent-bg' : 'hover:bg-raised')}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-ink">
                    {a.queuePosition && <span className="mr-1.5 font-mono text-xs text-muted">#{a.queuePosition}</span>}
                    {a.name}
                  </span>
                  <span className="flex gap-1">
                    {a.invited && <Pill tone="accent">Invited</Pill>}
                    <Pill tone={statusTone(a.status)}>{APPLICATION_STATUS_LABEL[a.status]}</Pill>
                  </span>
                </span>
                <span className="text-xs text-muted">
                  {a.email} · {a.marketsOfInterest.join(', ')} · {a.experienceYears ?? 0} yrs · {fmtTime(a.createdAt, tz, true)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Card>
      {selected ? <ApplicationDetail key={selected} id={selected} /> : <Card className="p-6 text-sub">Pick an application to review it.</Card>}
    </div>
  );
}

function historyLine(h: AppDetail['history'][number]) {
  const d = h.detail ?? {};
  const who = h.actor ?? 'The applicant';
  switch (h.action) {
    case 'application.submit':
      return `Applied${d.invited ? ' with an invite' : ''}`;
    case 'application.review_started':
      return `${who} started the review`;
    case 'application.more_info':
      return `${who} asked: “${String(d.message ?? '')}”`;
    case 'application.info_provided':
      return `Applicant answered: “${String(d.reply ?? '')}”${Array.isArray(d.changed) && d.changed.length ? ` (updated ${d.changed.join(', ')})` : ''}`;
    case 'application.reject':
      return `${who} rejected it: “${String(d.reason ?? '')}”`;
    case 'application.approve':
      return `${who} approved it`;
    case 'application.activation_resent':
      return `${who} sent a new activation link`;
    default:
      return h.action;
  }
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="m-0 text-ink2">{children || '—'}</dd>
    </div>
  );
}

function ApplicationDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const detail = useQuery({ queryKey: ['application', id], queryFn: () => api<AppDetail>(`/applications/${id}`) });
  const [mode, setMode] = useState<'approve' | 'info' | 'reject' | null>(null);
  const [text, setText] = useState('');
  const refresh = (d: AppDetail) => {
    qc.setQueryData(['application', id], d);
    void qc.invalidateQueries({ queryKey: ['applications'] });
    setMode(null);
    setText('');
  };
  const review = useMutation({ mutationFn: () => api<AppDetail>(`/applications/${id}/review`, { method: 'POST' }), onSuccess: refresh });
  const decide = useMutation({ mutationFn: (body: object) => api<AppDetail>(`/applications/${id}/decision`, { body }), onSuccess: refresh });
  const resend = useMutation({ mutationFn: () => api<AppDetail>(`/applications/${id}/activation-link`, { method: 'POST' }), onSuccess: refresh });

  // opening a new application moves it to "Under review" so the applicant sees someone is on it (once)
  const status = detail.data?.status;
  const started = useRef(false);
  const startReview = review.mutate;
  useEffect(() => {
    if (status !== 'SUBMITTED' || started.current) return;
    started.current = true;
    startReview();
  }, [status, startReview]);

  if (detail.isLoading) return <Card className="p-6 text-sub">Loading…</Card>;
  if (detail.error || !detail.data) return <ErrorText error={errorText(detail.error)} />;
  const a = detail.data;
  const isOpen = a.status === 'SUBMITTED' || a.status === 'UNDER_REVIEW' || a.status === 'MORE_INFO';

  return (
    <Card className="flex flex-col">
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {a.name}
            {a.invite && <Pill tone="accent">Invited by {a.invite.invitedBy}</Pill>}
          </span>
        }
        right={<Pill tone={statusTone(a.status)}>{APPLICATION_STATUS_LABEL[a.status]}</Pill>}
      />
      <div className="flex flex-col gap-4 p-4">
        <dl className="m-0 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Fact label="Email">{a.email}</Fact>
          <Fact label="Mobile">{a.phone}</Fact>
          <Fact label="Country">{a.country}</Fact>
          <Fact label="Timezone">{a.timezone}</Fact>
          <Fact label="Experience">{a.experienceYears != null ? `${a.experienceYears} year${a.experienceYears === 1 ? '' : 's'}` : null}</Fact>
          <Fact label="Markets">{a.marketsOfInterest.join(', ')}</Fact>
          <Fact label="Symbols">{a.preferredSymbols.join(', ')}</Fact>
          <Fact label="Brokers">{a.brokers}</Fact>
          <Fact label="Referred by">{a.referral}</Fact>
        </dl>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted">About their trading</span>
          <p className="m-0 rounded-lg bg-raised px-3.5 py-3 whitespace-pre-wrap text-ink2">{a.statement}</p>
        </div>
        <div className="flex flex-wrap gap-1.5" aria-label="Documents accepted">
          {a.acceptances.map((x) => (
            <Pill key={x.kind} tone="neutral">
              ✓ {x.label} v{x.version}
            </Pill>
          ))}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted">History</span>
          <ol className="m-0 flex list-none flex-col gap-1.5 border-l border-line2 pl-3">
            {a.history.map((h, i) => (
              <li key={i} className="flex flex-col">
                <span className="text-sm text-ink2">{historyLine(h)}</span>
                <span className="text-xs text-muted">{fmtTime(h.at, tz, true)}</span>
              </li>
            ))}
          </ol>
        </div>

        {a.status === 'APPROVED' && a.user && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line2 bg-raised px-3.5 py-3">
            {a.user.status === 'INVITED' ? (
              <>
                <span className="text-sm text-ink2">
                  Waiting for activation · link {a.activationExpiresAt && new Date(a.activationExpiresAt) > new Date() ? `expires ${fmtTime(a.activationExpiresAt, tz, true)}` : 'has expired'}
                </span>
                <Button onClick={() => resend.mutate()} disabled={resend.isPending}>
                  Send a new activation link
                </Button>
              </>
            ) : (
              <span className="text-sm text-ink2">
                Account {a.user.status.toLowerCase()} · last sign-in {a.user.lastLoginAt ? fmtTime(a.user.lastLoginAt, tz, true) : 'never'}
              </span>
            )}
          </div>
        )}
        <ErrorText error={resend.error ? errorText(resend.error) : null} />
        <Ok text={resend.isSuccess ? 'A new activation link was emailed.' : null} />

        {isOpen && (
          <div className="flex flex-col gap-3 border-t border-line pt-4">
            <div className="flex flex-wrap gap-2">
              <Button variant={mode === 'approve' ? 'primary' : 'success'} onClick={() => setMode(mode === 'approve' ? null : 'approve')} aria-expanded={mode === 'approve'}>
                Approve…
              </Button>
              <Button onClick={() => setMode(mode === 'info' ? null : 'info')} aria-expanded={mode === 'info'}>
                Ask for more info…
              </Button>
              <Button variant="danger" onClick={() => setMode(mode === 'reject' ? null : 'reject')} aria-expanded={mode === 'reject'}>
                Reject…
              </Button>
            </div>
            {mode === 'approve' && <ApprovePanel app={a} onApprove={(body) => decide.mutate({ decision: 'APPROVE', ...body })} busy={decide.isPending} />}
            {(mode === 'info' || mode === 'reject') && (
              <div className="flex flex-col gap-3 rounded-lg border border-line2 p-3.5">
                <Field label={mode === 'info' ? 'What do you need from them?' : 'Reason'} hint="The applicant sees this in their email and on their status page.">
                  <Textarea rows={3} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} />
                </Field>
                <Button
                  variant={mode === 'reject' ? 'danger' : 'primary'}
                  className="self-start"
                  disabled={decide.isPending || text.trim().length < 3}
                  onClick={() => decide.mutate(mode === 'info' ? { decision: 'MORE_INFO', message: text } : { decision: 'REJECT', reason: text })}
                >
                  {mode === 'info' ? 'Send to the applicant' : 'Reject application'}
                </Button>
              </div>
            )}
            <ErrorText error={decide.error ? errorText(decide.error) : null} />
          </div>
        )}
        {a.status === 'REJECTED' && a.decisionReason && <p className="m-0 text-sm text-sub">Reason given: {a.decisionReason}</p>}
      </div>
    </Card>
  );
}

interface NewAccount {
  code: string;
  name: string;
  marketId: string;
  currency: string;
  startingEquity: string;
}

function ApprovePanel({ app, onApprove, busy }: { app: AppDetail; onApprove: (body: object) => void; busy: boolean }) {
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api<AccountRow[]>('/accounts') });
  const markets = useQuery({ queryKey: ['markets'], queryFn: () => api<MarketRow[]>('/markets') });
  const instruments = useQuery({ queryKey: ['instruments', 'all'], queryFn: () => api<InstrumentRow[]>('/instruments') });
  const [roles, setRoles] = useState<string[]>(['TRADER']);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [newAccounts, setNewAccounts] = useState<NewAccount[]>([]);
  const [instrumentIds, setInstrumentIds] = useState<string[]>(app.suggestedInstrumentIds);
  const [note, setNote] = useState('');
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const initials = app.name.split(/\s+/).map((p) => p[0]?.toUpperCase() ?? '').join('').slice(0, 3);

  const interest = useMemo(() => {
    const byCode = new Map((markets.data ?? []).map((m) => [m.code, m]));
    const first = app.marketsOfInterest.map((c) => byCode.get(c)).filter(Boolean) as MarketRow[];
    return [...first, ...(markets.data ?? []).filter((m) => !app.marketsOfInterest.includes(m.code))];
  }, [markets.data, app.marketsOfInterest]);

  const addAccount = () => {
    const m = interest[0];
    if (!m) return;
    setNewAccounts([...newAccounts, { code: `${m.code}-${initials}`, name: `${app.name} · ${m.name}`, marketId: m.id, currency: m.currency, startingEquity: '' }]);
  };
  const setNew = (i: number, patch: Partial<NewAccount>) => setNewAccounts(newAccounts.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const isTrader = roles.includes('TRADER');
  const missing = isTrader && (accountIds.length + newAccounts.length === 0 ? 'Give a trader at least one account.' : instrumentIds.length === 0 ? 'Give a trader at least one symbol.' : null);

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-[#1e5a3c] p-3.5">
      <div className="flex flex-col gap-1.5">
        <span id="roles-label" className="text-xs text-muted">Roles</span>
        <div role="group" aria-labelledby="roles-label" className="flex flex-wrap gap-1.5">
          {[...ROLES].sort((x, y) => Number(y === 'TRADER') - Number(x === 'TRADER')).map((r) => (
            <label key={r} className={cx('flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm', roles.includes(r) ? 'border-accent bg-accent-bg text-ink' : 'border-line2 text-sub')}>
              <input type="checkbox" checked={roles.includes(r)} onChange={() => setRoles(toggle(roles, r))} />
              {roleLabel(r)}
            </label>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span id="accounts-label" className="text-xs text-muted">Accounts</span>
        <div role="group" aria-labelledby="accounts-label" className="flex max-h-[180px] flex-col gap-1 overflow-y-auto">
          {accounts.data
            ?.filter((x) => x.status === 'ACTIVE')
            .sort((x, y) => Number(app.marketsOfInterest.includes(y.market.code)) - Number(app.marketsOfInterest.includes(x.market.code)) || x.code.localeCompare(y.code))
            .map((x) => (
              <label key={x.id} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-raised">
                <input type="checkbox" checked={accountIds.includes(x.id)} onChange={() => setAccountIds(toggle(accountIds, x.id))} />
                <span className="font-mono text-sm">{x.code}</span>
                <span className="text-sm text-sub">
                  {x.market.code} · {x.currency} · {x.traders.length ? x.traders.map((t) => t.name).join(', ') : 'no trader yet'}
                </span>
              </label>
            ))}
        </div>
        {newAccounts.map((n, i) => (
          <div key={i} className="grid gap-2 rounded-lg bg-raised p-2.5 sm:grid-cols-[110px_1fr_110px_80px_130px_auto]">
            <Field label="Code"><Input value={n.code} onChange={(e) => setNew(i, { code: e.target.value.toUpperCase() })} /></Field>
            <Field label="Account name"><Input value={n.name} onChange={(e) => setNew(i, { name: e.target.value })} /></Field>
            <Field label="Market">
              <Select value={n.marketId} onChange={(e) => setNew(i, { marketId: e.target.value, currency: markets.data?.find((m) => m.id === e.target.value)?.currency ?? n.currency })}>
                {interest.map((m) => <option key={m.id} value={m.id}>{m.code}</option>)}
              </Select>
            </Field>
            <Field label="Currency"><Input value={n.currency} maxLength={3} onChange={(e) => setNew(i, { currency: e.target.value.toUpperCase() })} /></Field>
            <Field label="Starting equity"><Input mono inputMode="decimal" value={n.startingEquity} onChange={(e) => setNew(i, { startingEquity: e.target.value })} /></Field>
            <Button variant="ghost" className="self-end" aria-label={`Remove new account ${n.code}`} onClick={() => setNewAccounts(newAccounts.filter((_, j) => j !== i))}>✕</Button>
          </div>
        ))}
        <Button variant="ghost" className="self-start" onClick={addAccount}>+ New account</Button>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-muted">Symbols to analyse every day {app.preferredSymbols.length > 0 && <span>(they asked for {app.preferredSymbols.join(', ')})</span>}</span>
        {interest.map((m) => {
          const list = (instruments.data ?? []).filter((x) => x.marketId === m.id);
          if (!list.length) return null;
          return (
            <div key={m.id} role="group" aria-label={`${m.code} symbols`} className="flex flex-wrap items-center gap-1.5">
              <span className="w-14 font-mono text-xs text-muted">{m.code}</span>
              {list.map((x) => {
                const on = instrumentIds.includes(x.id);
                return (
                  <button key={x.id} type="button" aria-pressed={on} title={x.displayName} onClick={() => setInstrumentIds(toggle(instrumentIds, x.id))} className={cx('min-h-9 cursor-pointer rounded-full px-3 py-1 text-sm', on ? 'bg-accent font-semibold text-bg' : 'border border-line2 bg-raised text-sub')}>
                    {x.symbol}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <Field label="Note for the record (optional)">
        <Input maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {missing && <p className="m-0 text-sm text-warn">{missing}</p>}
      <Button variant="success" className="self-start" disabled={busy || !roles.length || !!missing} onClick={() => onApprove({ roles, accountIds, newAccounts, instrumentIds, note })}>
        {busy ? 'Approving…' : 'Approve and send activation link'}
      </Button>
      <p className="m-0 text-xs text-muted">They get an email with a one-time link to set their password and two-step sign-in. It works for 72 hours.</p>
    </div>
  );
}

// ───────────────────────── Invites ─────────────────────────

function Invites() {
  const qc = useQueryClient();
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const markets = useQuery({ queryKey: ['markets'], queryFn: () => api<MarketRow[]>('/markets') });
  const list = useQuery({ queryKey: ['invites'], queryFn: () => api<InviteRow[]>('/invites') });
  const [f, setF] = useState({ email: '', name: '', marketsOfInterest: [] as string[], note: '' });
  const [copied, setCopied] = useState(false);
  const create = useMutation({
    mutationFn: () => api<{ id: string; url: string; expiresAt: string; emailed: boolean }>('/invites', { body: f }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invites'] });
      setF({ email: '', name: '', marketsOfInterest: [], note: '' });
      setCopied(false);
    },
  });
  const revoke = useMutation({ mutationFn: (id: string) => api(`/invites/${id}/revoke`, { method: 'POST' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['invites'] }) });
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="grid items-start gap-3.5 xl:grid-cols-[2fr_3fr]">
      <Card className="flex flex-col gap-3 p-4">
        <h2 className="m-0 text-[15px] font-semibold">Invite someone</h2>
        <p className="m-0 text-sm text-sub">The link pre-fills the application form and puts it at the front of the queue. Everything here is optional.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Email" hint="We email the link too when you fill this in."><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        </div>
        <div className="flex flex-col gap-1.5">
          <span id="invite-markets" className="text-xs text-muted">Markets</span>
          <div role="group" aria-labelledby="invite-markets" className="flex flex-wrap gap-1.5">
            {markets.data?.map((m) => {
              const on = f.marketsOfInterest.includes(m.code);
              return (
                <button key={m.id} type="button" aria-pressed={on} onClick={() => setF({ ...f, marketsOfInterest: on ? f.marketsOfInterest.filter((x) => x !== m.code) : [...f.marketsOfInterest, m.code] })} className={cx('min-h-9 cursor-pointer rounded-full px-3 py-1 text-sm', on ? 'bg-accent font-semibold text-bg' : 'border border-line2 bg-raised text-sub')}>
                  {m.code}
                </button>
              );
            })}
          </div>
        </div>
        <Field label="Personal note (in the email)"><Textarea rows={2} maxLength={500} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <ErrorText error={create.error ? errorText(create.error) : null} />
        <Button variant="primary" className="self-start" onClick={() => create.mutate()} disabled={create.isPending}>Create invite link</Button>
        {create.data && (
          <div className="flex flex-col gap-2 rounded-lg border border-[#22385a] bg-accent-bg p-3">
            <span className="text-sm font-semibold">Invite link — copy it now, it won’t be shown again</span>
            <div className="flex gap-2">
              <Input aria-label="Invite link" readOnly value={create.data.url} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
              <Button onClick={() => void copy(create.data.url)}>{copied ? 'Copied' : 'Copy'}</Button>
            </div>
            <span className="text-xs text-sub">
              Works until {fmtTime(create.data.expiresAt, tz, true)}.{create.data.emailed ? ' We also emailed it.' : ''}
            </span>
          </div>
        )}
      </Card>
      <Card>
        <CardHeader title={`Invites · ${list.data?.length ?? 0}`} />
        {list.data && !list.data.length && <Empty>No invites yet.</Empty>}
        <ul className="m-0 flex list-none flex-col p-0">
          {list.data?.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0">
              <div className="flex min-w-0 flex-col">
                <span className="font-semibold">{i.name ?? i.email ?? 'Open invite'}</span>
                <span className="text-xs text-muted">
                  {[i.email && i.name ? i.email : null, i.marketsOfInterest.join(', ') || null, `by ${i.invitedBy}`, `created ${fmtTime(i.createdAt, tz, true)}`].filter(Boolean).join(' · ')}
                </span>
                {i.application && <span className="text-xs text-sub">Applied as {i.application.name} · {APPLICATION_STATUS_LABEL[i.application.status as ApplicationStatusCode]}</span>}
              </div>
              <div className="flex items-center gap-2">
                <Pill tone={i.state === 'OPEN' ? 'accent' : i.state === 'USED' ? 'up' : 'neutral'}>{i.state === 'OPEN' ? `Open until ${fmtTime(i.expiresAt, tz, true)}` : titleCase(i.state)}</Pill>
                {i.state === 'OPEN' && (
                  <Button variant="ghost" onClick={() => revoke.mutate(i.id)} disabled={revoke.isPending}>Revoke</Button>
                )}
              </div>
            </li>
          ))}
        </ul>
        <ErrorText error={revoke.error ? errorText(revoke.error) : null} />
      </Card>
    </div>
  );
}

// ───────────────────────── Members ─────────────────────────

function Members() {
  const qc = useQueryClient();
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const list = useQuery({ queryKey: ['members'], queryFn: () => api<MemberRow[]>('/members') });
  const [suspending, setSuspending] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: 'ACTIVE' | 'SUSPENDED'; reason?: string }) => api<{ status: string; openTrades?: number }>(`/users/${v.id}/status`, { method: 'PATCH', body: { status: v.status, reason: v.reason } }),
    onSuccess: (r, v) => {
      void qc.invalidateQueries({ queryKey: ['members'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
      const who = list.data?.find((m) => m.id === v.id)?.name ?? 'Member';
      setDone(r.status === 'SUSPENDED' ? `${who} is suspended.${r.openTrades ? ` ${r.openTrades} open trade${r.openTrades === 1 ? ' is' : 's are'} flagged on the risk desk.` : ''}` : `${who} can sign in again.`);
      setSuspending(null);
      setReason('');
    },
  });

  return (
    <Card>
      <CardHeader title={`Members · ${list.data?.length ?? 0}`} right={<span className="text-xs text-muted">Suspending keeps all history; staff still see their work.</span>} />
      <div className="px-4 pt-3">
        <Ok text={done} />
        <ErrorText error={setStatus.error ? errorText(setStatus.error) : null} />
      </div>
      <ul className="m-0 flex list-none flex-col p-0">
        {list.data?.map((m) => (
          <li key={m.id} className="flex flex-col gap-2 border-t border-[#1a1f27] px-4 py-3 first:border-t-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-col">
                <span className="font-semibold">{m.name}</span>
                <span className="text-xs text-muted">
                  {m.email} · last sign-in {m.lastLoginAt ? fmtTime(m.lastLoginAt, tz, true) : 'never'}
                  {m.openTrades > 0 && ` · ${m.openTrades} open trade${m.openTrades === 1 ? '' : 's'}`}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                {m.roles.map((r) => <Pill key={r} tone="accent">{roleLabel(r)}</Pill>)}
                <Pill tone={statusTone(m.status)}>{m.status === 'INVITED' ? 'Not activated' : titleCase(m.status)}</Pill>
                {m.id !== me.data?.id && (m.status === 'ACTIVE' || m.status === 'INVITED') && suspending !== m.id && <Button variant="danger" onClick={() => { setSuspending(m.id); setDone(null); }}>Suspend…</Button>}
                {m.status === 'SUSPENDED' && <Button onClick={() => setStatus.mutate({ id: m.id, status: 'ACTIVE' })} disabled={setStatus.isPending}>Reactivate</Button>}
              </div>
            </div>
            {suspending === m.id && (
              <div className="flex flex-col gap-2 rounded-lg border border-[#6a2a27] bg-down-bg/40 p-3">
                <p className="m-0 text-sm text-ink2">
                  {m.status === 'INVITED' ? `${m.name}’s activation link stops working and they won’t be able to sign in.` : `${m.name} will be signed out everywhere, won’t be able to sign in and won’t get alerts.`}
                  {m.openTrades > 0 ? ` Their ${m.openTrades} open trade${m.openTrades === 1 ? '' : 's'} will be flagged for the Risk Manager.` : ''}
                </p>
                <Field label="Reason (kept in the audit log)">
                  <Input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <div className="flex gap-2">
                  <Button variant="danger" disabled={reason.trim().length < 3 || setStatus.isPending} onClick={() => setStatus.mutate({ id: m.id, status: 'SUSPENDED', reason })}>Suspend {m.name.split(' ')[0]}</Button>
                  <Button variant="ghost" onClick={() => setSuspending(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ───────────────────────── Floor documents ─────────────────────────

function Documents() {
  const list = useQuery({ queryKey: ['admin-policies'], queryFn: () => api<PolicyOverview[]>('/admin/policies') });
  return (
    <div className="flex flex-col gap-3.5">
      <p className="m-0 text-sm text-sub">Publishing a new version asks every member to read and accept it before they can use the floor again. Old versions and every acceptance are kept.</p>
      <ErrorText error={list.error ? errorText(list.error) : null} />
      {list.data?.map((p) => <DocumentEditor key={`${p.kind}:${p.current?.id ?? 'none'}`} doc={p} />)}
    </div>
  );
}

function DocumentEditor({ doc }: { doc: PolicyOverview }) {
  const qc = useQueryClient();
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [title, setTitle] = useState(doc.current?.title ?? doc.label);
  const [body, setBody] = useState(doc.current?.body ?? '');
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const changed = title !== (doc.current?.title ?? '') || body !== (doc.current?.body ?? '');
  const next = (doc.current?.version ?? 0) + 1;
  const publish = useMutation({
    mutationFn: () => api('/policies', { body: { kind: doc.kind, title, body } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-policies'] });
      void qc.invalidateQueries({ queryKey: ['me-policies'] });
    },
  });

  return (
    <Card className="flex flex-col">
      <CardHeader
        title={`${doc.label}${doc.current ? ` · version ${doc.current.version}` : ''}`}
        right={doc.current && <span className="text-xs text-muted">Accepted by {doc.acceptedMembers} of {doc.activeMembers} active members · published {fmtDate(doc.current.publishedAt, tz)}</span>}
      />
      <div className="grid gap-4 p-4 xl:grid-cols-[3fr_1fr]">
        {editing ? (
          <div className="flex flex-col gap-3">
            <Field label="Title"><Input maxLength={160} value={title} onChange={(e) => { setTitle(e.target.value); setConfirm(false); }} /></Field>
            <Field label="Text" hint="Plain text. Line breaks are kept.">
              <Textarea rows={14} maxLength={50_000} value={body} onChange={(e) => { setBody(e.target.value); setConfirm(false); }} className="font-mono text-[13px]" />
            </Field>
            <ErrorText error={publish.error ? errorText(publish.error) : null} />
            {confirm ? (
              <div className="flex flex-col gap-2 rounded-lg border border-[#5a4316] bg-warn-bg p-3">
                <span className="text-sm text-warn">Publish version {next}? Everyone except you will have to accept it before they can use the floor again.</span>
                <div className="flex gap-2">
                  <Button variant="primary" onClick={() => publish.mutate()} disabled={publish.isPending}>{publish.isPending ? 'Publishing…' : `Publish version ${next}`}</Button>
                  <Button variant="ghost" onClick={() => setConfirm(false)}>Not yet</Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button variant="primary" disabled={!changed || title.trim().length < 3 || body.trim().length < 20} onClick={() => setConfirm(true)}>Publish as version {next}…</Button>
                <Button variant="ghost" onClick={() => { setEditing(false); setTitle(doc.current?.title ?? doc.label); setBody(doc.current?.body ?? ''); }}>Cancel</Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {doc.current ? <PolicyText body={doc.current.body} className="max-h-[260px] overflow-y-auto rounded-lg bg-raised px-3.5 py-3" /> : <Empty>No text yet.</Empty>}
            <Button className="self-start" onClick={() => setEditing(true)}>Edit text</Button>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted">Versions</span>
          <ol className="m-0 flex list-none flex-col gap-1 p-0">
            {doc.versions.map((v) => (
              <li key={v.id} className="flex flex-col gap-0.5 rounded-lg bg-raised px-2.5 py-1.5 text-sm">
                <span className="flex items-center gap-2">
                  <span className="font-mono">v{v.version}</span> {v.isCurrent && <Pill tone="up">current</Pill>}
                </span>
                <span className="text-xs text-muted">
                  {fmtDate(v.publishedAt, tz).replace(/^\w+, /, '')} · {v.acceptances} member{v.acceptances === 1 ? '' : 's'} accepted
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Card>
  );
}
